import React, { useCallback, useEffect, useMemo, useState } from "react";
import { FORMS, MODULES } from "../../../../const";
import {
  CurrencyRenderer,
  ResizableScrollingTextRenderer,
} from "../../../renderer/StyleRenderer";
import ButtonRenderer from "../../../renderer/ButtonRenderer";
import { ChevronDown, ChevronUp } from "react-feather";
import Grid from "../../../Grid";
import Input from "../../../input/Input";
import Button from "../../../input/Button";
import {
  getBulkDropdownData,
  getOtherRefDetails,
} from "../../../../api/keyvalue";
import Dropdown from "../../../input/Dropdown";
import { FiChevronsRight } from "react-icons/fi";
import { useDispatch, useSelector } from "react-redux";
import { taxCalculate } from "../../../../api/finance/chargeConfirmation";
import { invEvActions } from "../../../../store/finance/inv-ev/inv-ev-slice";
import {
  saveConsolidatedBulkInvEv,
  savePlantWiseBulkInvEv,
} from "../../../../store/finance/inv-ev/inv-ev-actions";
import LoadingSpinner from "../../../LoadingSpinner";
import useUserCompany from "../../../../hooks/useUserCompany";
import ChargeTaxBreakdown from "../ChargeTaxBreakdown";
import SearchDropdown from "../../../input/SearchDropdown";

const gridHeaderConst = {
  CHARGE: "CHARGE-HEADER",
};

const HEADER_FORM = FORMS.EV_INV_MANAGER.HEADER.form;

const BulkTaxBreakdown = ({
  commonNoConsolidationView,
  commonConsolidated,
  formatter,
}) => {
  const userCompany = useUserCompany();
  const companyId = userCompany?.comId ?? -1;
  const auth = useSelector((state) => state.user.permissions[MODULES.Finance]);
  const evInvHeaderPermissions = auth[HEADER_FORM];
  const bulkGenEvPermission = evInvHeaderPermissions?.FORM?.BULK_GEN_EV;
  const bulkGenInvPermission = evInvHeaderPermissions?.FORM?.BULK_GEN_INV;
  const changeTransporter =
    evInvHeaderPermissions?.FORM?.CHANGE_TRANSPORTER_BULK;
  const changeCustomer = evInvHeaderPermissions?.FORM?.CHANGE_CUSTOMER_BULK;

  const [invCustomer, setInvCustomer] = useState(null);
  const [invCustomerName, setInvCustomerName] = useState(null);
  const [transporter, setTransporter] = useState(null);
  const [transporterName, setTransporterName] = useState(null);
  const [resetField, setResetField] = useState(null);

  const isEditMode = useSelector((state) => state.invEv.editMode);
  const categoryId = useSelector((state) => state.invEv.categoryId);
  const withoutTaxTotal = useSelector((state) => state.invEv.withoutTaxTotal);
  const withTaxTotal = useSelector((state) => state.invEv.withTaxTotal);
  const taxAmount = useSelector((state) => state.invEv.taxAmount);
  const charges = useSelector((state) => state.invEv.charges);
  const fromDate = useSelector((state) => state.invEv.fromDate);
  const toDate = useSelector((state) => state.invEv.toDate);

  const hasShipperWiseData = useSelector(
    (state) => state.invEv.hasShipperWiseData,
  );

  const [shipperWiseData, setShipperWiseData] = useState({});
  const [shipperWiseSubmitData, setShipperWiseSubmitData] = useState({});
  const [hasMultipleShippers, setHasMultipleShippers] = useState(false);
  const stakeholderId = useSelector((state) => state.invEv.stakeholderId);
  const stakeholderName = useSelector((state) => state.invEv.stakeholderName);
  const currencyId = useSelector((state) => state.invEv.currencyId);
  const statusUpdateRequest = useSelector(
    (state) => state.invEv.statusUpdateRequest,
  );

  const [selectedCharge, setSelectedCharge] = useState(null);
  const user = useSelector((state) => state.user);
  // All charge data
  const [bulkChargeData, setBulkChargeData] = useState([]);
  // Charge data with headers
  const [bulkGridData, setBulkGridData] = useState([]);
  const [groupedData, setGroupedData] = useState({});
  const [generationTypeOptions, setGenerationTypeOptions] = useState([]);
  const [generationType, setGenerationType] = useState(1);
  const [chargeDataChanged, setChargeDataChanged] = useState(false);
  const [consolidatedView, setConsolidatedView] = useState(3);

  const [consolidateOptions, setConsolidateOptions] = useState([]);
  const memoizedBulkGridData = useMemo(
    () => bulkGridData.filter((data) => !data.hide),
    [bulkGridData],
  );

  const [consolidateOptionsLoading, setConsolidateOptionsLoading] =
    useState(false);

  const [isLoading, setIsLoading] = useState(false);
  const [isDataLoading, setIsDataLoading] = useState(false);

  const [statusOptions, setStatusOptions] = useState([]);
  const [status, setStatus] = useState(2);
  const [remark, setRemark] = useState("");

  const [consolidatedRequest, setConsolidatedRequest] = useState(null);
  const isSubmitLoading = useSelector((state) => state.invEv.submitLoading);

  const dispatch = useDispatch();

  useEffect(() => {
    if (categoryId === 1) {
      setInvCustomer(stakeholderId);
      setInvCustomerName(stakeholderName);
    } else {
      setTransporter(stakeholderId);
      setTransporterName(stakeholderName);
    }
  }, [stakeholderId, stakeholderName, categoryId]);

  const getSubmitData = async (data, stakeholder) => {
    const taxData = await getBulkTax(data, stakeholder);

    return {
      jobEvInvoiceHeaderRequest: {
        eihCompany: companyId,
        eihSingleBulk: 2,
        eihType: categoryId,
        eihInvCustomer: categoryId === 1 ? stakeholderId : null,
        eihTransporter: categoryId === 2 ? stakeholderId : null,
        eihBank: null,
        eihExchange: null,
        eihCurrency: currencyId,
        eihRemarks: "",
        eihWithoutTax: taxData.totalWithoutTax,
        eihWithTax: taxData.total,
        eihStatus: status,
        eihActive: 1,
        type: categoryId,
        eihAdHoc: 2,
      },
      jobEvInvoiceDetailRequests: data.map((chg) => {
        const chargeTaxRes = taxData?.taxResponses?.find(
          (tr) => tr.chdId === chg.chdId,
        );
        const chargeTax = chargeTaxRes ? chargeTaxRes.taxAmount : 0.0;

        return {
          eidPickChargeId: chg.chdId,
          eidJob: chg.job,
          eidChhId: chg.chdChargeId,
          eidCharge: chg.chdChacrges,
          eidDescription: chg.chdDescription?.toString(),
          eidCurrency: currencyId,
          eidTotal: chg.chdTotal,
          eidQty: chg.chdQty,
          eidValue: chg.chdValue,
          eidUom: chg.chdUom,
          eidTax: true,
          eidTaxAmount: chargeTax,
          eidExcId: chg.chdExcId,
          eidExcRate: chg.chdExcRate,
          jobEvInvoicesTaxDetailRequestList:
            chargeTaxRes.jobEvInvoicesTaxDetailRequestList,
          eidStatus: 2,
          eidActive: 1,
        };
      }),
      statusUpdateRequest,
      author: user.username,
    };
  };

  const getBulkTax = useCallback(
    async (charges, stakeholder) => {
      const taxRequests = charges.map((charge) => ({
        chdId: charge.chdId,
        amount: charge.chdTotal ?? 0.0,
      }));

      const {
        total,
        withTaxTotal: totalWithTax,
        totalTax,
        taxResponses,
      } = await taxCalculate({
        stakeholderId: stakeholder,
        taxRequests,
        isAdHoc: false,
      });
      return {
        total: totalWithTax,
        totalWithoutTax: total,
        totalTax,
        taxResponses: taxResponses.map((tr) => ({
          ...tr,
          jobEvInvoicesTaxDetailRequestList: tr.taxInfoList
            ? tr.taxInfoList.map((ti) => ({
                eitCompany: companyId,
                eitTad: ti.tadId,
                eitTax: ti.taxId,
                taxName: ti.taxTax,
                eitTadValue: ti.taxPercentage,
                eitValue: ti.taxAmount,
                eitStatus: 2,
                eitActive: 1,
                beforeTax: ti.beforeTaxAmount,
                afterTax: ti.afterTaxAmount,
                author: user.username,
              }))
            : [],
        })),
      };
    },
    [stakeholderId],
  );

  useEffect(() => {
    if (
      isEditMode ||
      !charges ||
      !stakeholderId ||
      (categoryId === 1 && !invCustomer) ||
      (categoryId === 2 && !transporter)
    ) {
      return;
    }
    (async () => {
      setIsDataLoading(true);
      const evInvDetails = [];
      const chargeDetails = [];
      const taxData = await getBulkTax(
        charges,
        categoryId === 1 ? invCustomer : transporter,
      );
      const wtTotal =
        taxData?.total === null || taxData?.total === undefined
          ? 0.0
          : taxData?.total;
      const wotTotal =
        taxData?.totalWithoutTax === null ||
        taxData?.totalWithoutTax === undefined
          ? 0.0
          : taxData?.totalWithoutTax;

      let shipperData = {};
      let submitShipperData = {};

      for (let charge of charges) {
        const chargeTaxRes = taxData?.taxResponses?.find(
          (tr) => tr.chdId === charge.chdId,
        );
        const chargeTax = chargeTaxRes ? chargeTaxRes.taxAmount : 0.0;

        evInvDetails.push({
          eidPickChargeId: charge.chdId,
          eidJob: charge.job,
          eidChhId: charge.chdChargeId,
          eidCharge: charge.chdChacrges,
          eidDescription: charge.chdDescription?.toString(),
          eidCurrency: currencyId,
          eidTotal: charge.chdTotal,
          eidQty: charge.chdQty,
          eidValue: charge.chdValue,
          eidUom: charge.chdUom,
          eidTax: true,
          eidTaxAmount: chargeTax,
          eidExcId: charge.chdExcId,
          eidExcRate: charge.chdExcRate,
          eidStatus: 2,
          eidActive: 1,
          jobEvInvoicesTaxDetailRequestList:
            chargeTaxRes.jobEvInvoicesTaxDetailRequestList,
        });

        setConsolidatedRequest({
          jobEvInvoiceHeaderRequest: {
            eihSingleBulk: 2,
            eihType: categoryId,
            eihCompany: companyId,
            eihInvCustomer: categoryId === 1 ? stakeholderId : null,
            eihTransporter: categoryId === 2 ? stakeholderId : null,
            eihBank: null,
            eihExchange: null,
            eihCurrency: currencyId,
            eihRemarks: "",
            eihWithoutTax: wotTotal,
            eihWithTax: wtTotal,
            eihStatus: 2,
            eihActive: 1,
            type: categoryId,
            eihAdHoc: 2,
          },
          jobEvInvoiceDetailRequests: evInvDetails,
          statusUpdateRequest,
          author: user.username,
        });

        const mappedCharge = {
          ...charge,
          chdTaxAmount: chargeTax,
          chdTotalWithTax: chargeTax + charge.chdTotal,
          jobEvInvoicesTaxDetailRequestList:
            chargeTaxRes.jobEvInvoicesTaxDetailRequestList,
          hide: false,
        };

        chargeDetails.push(mappedCharge);

        if (hasShipperWiseData) {
          shipperData = {
            ...shipperData,
            [mappedCharge.shipperName]: shipperData[mappedCharge.shipperName]
              ? [...shipperData[mappedCharge.shipperName], mappedCharge]
              : [mappedCharge],
          };

          submitShipperData = {
            ...submitShipperData,
            [mappedCharge.shipper]: submitShipperData[mappedCharge.shipper]
              ? [...submitShipperData[mappedCharge.shipper], mappedCharge]
              : [mappedCharge],
          };
        }
      }
      setHasMultipleShippers(Object.keys(submitShipperData).length > 1);
      setShipperWiseSubmitData(submitShipperData);
      setShipperWiseData(shipperData);
      dispatch(
        invEvActions.setTaxInfo({
          withoutTaxTotal: wotTotal,
          withTaxTotal: wtTotal,
          taxAmount: wtTotal - wotTotal,
        }),
      );

      setBulkChargeData(chargeDetails);
      setBulkGridData(chargeDetails);
      setIsDataLoading(false);
    })();
  }, [
    categoryId,
    charges,
    companyId,
    currencyId,
    dispatch,
    getBulkTax,
    hasShipperWiseData,
    invCustomer,
    isEditMode,
    stakeholderId,
    statusUpdateRequest,
    transporter,
    user.username,
  ]);

  useEffect(() => {
    (async () => {
      setConsolidateOptionsLoading(true);
      const { consolidate_charge: consolidateTypes } =
        await getBulkDropdownData([{ key: "consolidate_charge" }]);
      setConsolidateOptions(consolidateTypes);
      setConsolidateOptionsLoading(false);
    })();
  }, []);

  useEffect(() => {
    (async () => {
      setIsLoading(true);
      const { generation_type: generationOptions, status: statusData } =
        await getBulkDropdownData([
          { key: "status" },
          { key: "generation_type" },
        ]);
      setGenerationTypeOptions(generationOptions);
      setStatusOptions(statusData);
      setIsLoading(false);
    })();
  }, []);

  useEffect(() => {
    if (consolidatedView !== 3) {
      const currentGridData = [];
      let currentId = 2;
      for (let [header, charges] of Object.entries(groupedData)) {
        currentGridData.push({
          currId: currentId,
          section: gridHeaderConst.CHARGE,
          chgCharges: header,
          chdTotal: charges.charges.reduce(
            (total, charge) => total + charge.chdTotal,
            0,
          ),
          chdTaxAmount: charges.charges.reduce(
            (tax, charge) => tax + charge.chdTaxAmount,
            0,
          ),
          chdTotalWithTax: charges.charges.reduce(
            (tax, charge) => tax + charge.chdTotalWithTax,
            0,
          ),
          chdSelected: charges.charges.every((chg) => chg.chdSelected)
            ? true
            : charges.charges.some((chg) => chg.chdSelected)
              ? null
              : false,
          consolidateChargeCount: charges.charges.length,
          hide: false,
          showCharges: charges.showCharges,
        });
        currentId += 1;
        for (let cuChg of charges.charges) {
          currentGridData.push({ ...cuChg, currId: currentId });
          currentId += 1;
        }
      }
      setBulkGridData(currentGridData);
    }
  }, [consolidatedView, isEditMode, groupedData, bulkChargeData]);

  const isChargeHeaderRow = (params) => {
    return params.data.section === gridHeaderConst.CHARGE;
  };

  const isChargeHeaderRowConsolidated = (params) => {
    return (
      params.data.section === gridHeaderConst.CHARGE &&
      params.data.consolidateChargeCount > 1
    );
  };

  // Col-span rule
  let chargeCellClassRules = useMemo(
    () => ({
      "header-cell": 'data.section === "CHARGE-HEADER"',
    }),
    [],
  );

  const withTaxConsolidatedViewConfig = useMemo(
    () => [
      ...commonConsolidated,
      {
        headerName: "TAX",
        field: "chdTaxAmount",
        authenticated: true,
        module: MODULES.Finance,
        primaryForm: FORMS.EV_INV_MANAGER.DETAILS.form,
        cellClassRules: chargeCellClassRules,
        cellRendererSelector: (param) => {
          if (param.data.chdTaxAmount === -1) {
            return {
              component: () => "Not yet calculated",
            };
          }
          return {
            component: CurrencyRenderer,
            params: {
              formatter: formatter,
            },
          };
        },
      },
      {
        headerName: "With Tax Total",
        field: "chdTotalWithTax",
        authenticated: true,
        module: MODULES.Finance,
        primaryForm: FORMS.EV_INV_MANAGER.DETAILS.form,
        cellClassRules: chargeCellClassRules,
        cellRendererSelector: (param) => {
          if (isChargeHeaderRow(param)) {
            if (param.data.chdTaxAmount === -1) {
              return {
                component: () => "Not yet calculated",
              };
            } else {
              return {
                component: CurrencyRenderer,
                params: {
                  formatter: formatter,
                  otherProp: true,
                  currentValue: param.data.chdTaxAmount + param.data.chdTotal,
                },
              };
            }
          }
          if (param.data.chdTotalWithTax === -1) {
            return {
              component: () => "Not yet calculated",
            };
          }
          return {
            component: CurrencyRenderer,
            params: {
              formatter: formatter,
            },
          };
        },
      },
      {
        field: "Show Charges",
        cellClassRules: chargeCellClassRules,
        cellRendererSelector: (param) => {
          if (!isChargeHeaderRowConsolidated(param)) {
            return null;
          }
          return {
            component: ButtonRenderer,
            params: {
              clickHandler: () => {
                setGroupedData((prevState) => ({
                  ...prevState,
                  [param.data.chgCharges]: {
                    ...prevState[param.data.chgCharges],
                    showCharges: !prevState[param.data.chgCharges].showCharges,
                    charges: prevState[param.data.chgCharges].charges.map(
                      (chg) => ({
                        ...chg,
                        hide: !chg.hide,
                      }),
                    ),
                  },
                }));
              },
              label: `${param.data.consolidateChargeCount} Charges`,
              Icon: param.data.showCharges ? ChevronUp : ChevronDown,
              className: "ui primary button",
            },
          };
        },
      },
    ],
    [chargeCellClassRules, commonConsolidated, formatter],
  );

  const noConsolidationViewWithTax = useMemo(
    () => [
      {
        headerName: "Charges",
        field: "chgCharges",
        authenticated: true,
        module: MODULES.Finance,
        primaryForm: FORMS.EV_INV_MANAGER.DETAILS.form,
        cellRenderer: ResizableScrollingTextRenderer,
        cellRendererParams: (params) => ({
          threshold: 15,
        }),
      },
      ...commonNoConsolidationView,
      {
        headerName: "TAX",
        field: "chdTaxAmount",
        authenticated: true,
        module: MODULES.Finance,
        primaryForm: FORMS.EV_INV_MANAGER.DETAILS.form,
        cellRendererSelector: (param) => {
          if (param.data.chdTaxAmount === -1) {
            return {
              component: () => "Not yet calculated",
            };
          }
          return {
            component: CurrencyRenderer,
            params: {
              formatter: formatter,
            },
          };
        },
      },
      {
        headerName: "With Tax Total",
        field: "chdTotalWithTax",
        authenticated: true,
        module: MODULES.Finance,
        primaryForm: FORMS.EV_INV_MANAGER.DETAILS.form,
        cellClassRules: chargeCellClassRules,
        cellRendererSelector: (param) => {
          if (param.data.chdTotalWithTax === -1) {
            return {
              component: () => "Not yet calculated",
            };
          }
          return {
            component: CurrencyRenderer,
            params: {
              formatter: formatter,
            },
          };
        },
      },
    ],
    [chargeCellClassRules, commonNoConsolidationView, formatter],
  );

  const [gridConfig, setGridConfig] = useState(noConsolidationViewWithTax);

  const mapGroupData = useCallback(
    (data) => {
      switch (data) {
        case 1: {
          let gridObject = {};
          for (let charge of bulkChargeData) {
            gridObject = {
              ...gridObject,
              [charge.jobCode]: {
                showCharges: false,
                charges: gridObject[charge.jobCode]
                  ? [
                      ...gridObject[charge.jobCode].charges,
                      { ...charge, groupRef: charge.jobCode, hide: true },
                    ]
                  : [{ ...charge, groupRef: charge.jobCode, hide: true }],
              },
            };
          }
          setGridConfig(withTaxConsolidatedViewConfig);
          setGroupedData(gridObject);
          break;
        }
        case 2: {
          let gridObject = {};
          for (let charge of bulkChargeData) {
            gridObject = {
              ...gridObject,
              [charge.chgCharges]: {
                showCharges: false,
                charges: gridObject[charge.chgCharges]
                  ? [
                      ...gridObject[charge.chgCharges].charges,
                      { ...charge, groupRef: charge.chgCharges, hide: true },
                    ]
                  : [{ ...charge, groupRef: charge.chgCharges, hide: true }],
              },
            };
          }
          setGridConfig(withTaxConsolidatedViewConfig);
          setGroupedData(gridObject);
          break;
        }
        case 3: {
          setGridConfig(noConsolidationViewWithTax);
          setBulkGridData(bulkChargeData);
          break;
        }
        case 4: {
          let gridObject = {};

          for (let [shipperId, charges] of Object.entries(shipperWiseData)) {
            gridObject = {
              ...gridObject,
              [shipperId]: {
                showCharges: false,
                charges: charges.map((chg) => ({
                  ...chg,
                  groupRef: shipperId,
                  hide: true,
                })),
              },
            };
          }
          setGridConfig(withTaxConsolidatedViewConfig);
          setGroupedData(gridObject);

          break;
        }
        default:
      }
    },
    [bulkChargeData],
  );
  useEffect(() => {
    if (chargeDataChanged) {
      mapGroupData(withTaxConsolidatedViewConfig);
      setChargeDataChanged(false);
    }
  }, [chargeDataChanged, withTaxConsolidatedViewConfig, mapGroupData]);

  const actionDropdownHandler = useCallback(
    (data) => {
      mapGroupData(data);
      setConsolidatedView(data);
    },
    [mapGroupData],
  );

  const submitHandler = async () => {
    if (hasMultipleShippers && generationType === 2) {
      for (let shipperCharges of Object.values(shipperWiseSubmitData)) {
        const request = await getSubmitData(
          shipperCharges,
          categoryId === 1 ? invCustomer : transporter,
        );
        dispatch(
          savePlantWiseBulkInvEv({
            ...request,
            jobEvInvoiceHeaderRequest: {
              ...request.jobEvInvoiceHeaderRequest,
              eihInvCustomer:
                categoryId === 1
                  ? invCustomer
                  : request.jobEvInvoiceHeaderRequest?.eihInvCustomer,
              eihTransporter:
                categoryId === 2
                  ? transporter
                  : request.jobEvInvoiceHeaderRequest?.eihTransporter,
              eihFromDate: fromDate,
              eihToDate: toDate,
              eihRemarks: remark,
              eihStatus: status,
            },
          }),
        );
      }
    } else {
      dispatch(
        saveConsolidatedBulkInvEv({
          ...consolidatedRequest,
          jobEvInvoiceHeaderRequest: {
            ...consolidatedRequest.jobEvInvoiceHeaderRequest,
            eihInvCustomer:
              categoryId === 1
                ? invCustomer
                : consolidatedRequest.jobEvInvoiceHeaderRequest?.eihInvCustomer,
            eihTransporter:
              categoryId === 2
                ? transporter
                : consolidatedRequest.jobEvInvoiceHeaderRequest?.eihTransporter,
            eihFromDate: fromDate,
            eihToDate: toDate,
            eihRemarks: remark,
            eihStatus: status,
          },
        }),
      );
    }
    dispatch(invEvActions.resetState());
  };

  return (
    <>
      {isDataLoading ? (
        <LoadingSpinner />
      ) : (
        <div className="ui segment">
          <h3 className="ui header">
            {categoryId === 1 ? "Invoice" : "Expense Voucher"} Breakdown
          </h3>

          <div className="ui form" style={{ marginTop: "10px" }}>
            <>
              {!isEditMode && (
                <>
                  <div className="ui segment">
                    <div className="three fields">
                      <Input
                        field={"eih_without_tax"}
                        module={MODULES.Finance}
                        editMode={isEditMode}
                        form={HEADER_FORM}
                        type="text"
                        label="Without Tax"
                        placeholder="Without Tax"
                        readOnly={true}
                        value={
                          formatter
                            ? formatter.format(withoutTaxTotal)
                            : withTaxTotal
                        }
                      />
                      <Input
                        field={"eih_with_tax"}
                        module={MODULES.Finance}
                        editMode={isEditMode}
                        form={HEADER_FORM}
                        type="text"
                        label="Tax"
                        placeholder="Tax"
                        readOnly={true}
                        value={
                          formatter ? formatter.format(taxAmount) : taxAmount
                        }
                      />
                      <Input
                        field={"eih_total"}
                        module={MODULES.Finance}
                        editMode={isEditMode}
                        form={HEADER_FORM}
                        type="text"
                        label="Total"
                        placeholder="Total"
                        readOnly={true}
                        value={
                          formatter
                            ? formatter.format(withTaxTotal)
                            : withTaxTotal
                        }
                      />
                    </div>
                  </div>
                  <ChargeTaxBreakdown
                    formatter={formatter}
                    selectedCharge={
                      selectedCharge
                        ? {
                            ...selectedCharge,
                            jobEvInvoicesTaxDetailList:
                              selectedCharge.jobEvInvoicesTaxDetailRequestList,
                          }
                        : null
                    }
                  />
                  <Grid
                    module={MODULES.Finance}
                    showActionMenu={true}
                    cellClickHandler={(e) => {
                      setSelectedCharge(e.data);
                    }}
                    actionMenuOptions={
                      categoryId === 1 && hasShipperWiseData
                        ? [
                            ...consolidateOptions,
                            { key: 4, text: "Shipper", value: 4 },
                          ]
                        : consolidateOptions
                    }
                    actionMenuLoading={consolidateOptionsLoading}
                    dropdown={true}
                    dropdownValue={consolidatedView}
                    dropdownOptions={{
                      label: "Consolidated View",
                      multiple: false,
                      clearable: false,
                      search: false,
                    }}
                    heading={`${categoryId === 1 ? "Invoice" : "Expense Voucher"} Breakdown`}
                    handleActionMenuChange={(e, data) => {
                      actionDropdownHandler(data.value);
                    }}
                    content={memoizedBulkGridData}
                    config={gridConfig}
                  />
                </>
              )}
            </>
          </div>
          <div className="ui form" style={{ marginTop: "10px" }}>
            <div
              className={
                (categoryId === 1 && changeCustomer) ||
                (categoryId === 2 && changeTransporter)
                  ? categoryId === 1 &&
                    Object.entries(shipperWiseData).length > 1
                    ? "four fields"
                    : "three fields"
                  : categoryId === 1 &&
                      Object.entries(shipperWiseData).length > 1
                    ? "three fields"
                    : "two fields"
              }
            >
              {categoryId === 1 && hasMultipleShippers && (
                <Dropdown
                  required={true}
                  clearable={false}
                  label="Generation Type"
                  placeholder="Generation Type"
                  options={generationTypeOptions}
                  disabled={!hasMultipleShippers}
                  style={{ height: "20px" }}
                  value={generationType}
                  onChange={(e, data) => {
                    setGenerationType(data.value);
                  }}
                />
              )}
              {((categoryId === 1 && changeCustomer) ||
                (categoryId === 2 && changeTransporter)) && (
                <SearchDropdown
                  key={1}
                  editMode={false}
                  reset={resetField}
                  setReset={setResetField}
                  onResultSelect={(data) => {
                    if (categoryId === 1) {
                      setInvCustomer(data.value);
                      setInvCustomerName(data.title);
                    } else {
                      setTransporter(data.value);
                      setTransporterName(data.title);
                    }
                  }}
                  returnFullObject={true}
                  api={getOtherRefDetails}
                  multiGrid={true}
                  multiGridCurrentValue={
                    categoryId === 1 ? invCustomerName : transporterName
                  }
                  apiDependency={{
                    type: "stakeholder_type",
                    value:
                      categoryId === 1 ? "Invoicee Customer" : "Transporter",
                  }}
                  label={categoryId === 1 ? "Invoicee Customer" : "Transporter"}
                />
              )}
              <Input
                field={"eih_remarks"}
                module={MODULES.Finance}
                editMode={isEditMode}
                form={HEADER_FORM}
                authenticated={true}
                type="text"
                label="Remarks"
                placeholder="Remarks"
                value={remark}
                onChange={(e) => setRemark(e.target.value)}
              />
              <Dropdown
                field={"eih_status"}
                module={MODULES.Finance}
                editMode={isEditMode}
                form={HEADER_FORM}
                authenticated={true}
                required={true}
                clearable={false}
                readOnly={isEditMode || isLoading}
                label="Status"
                placeholder={isLoading ? "Loading ..." : "Status"}
                options={statusOptions}
                style={{ height: "20px" }}
                value={status}
                onChange={(e, data) => {
                  setStatus(data.value);
                }}
              />
            </div>
            <div
              className="two fields"
              style={{
                display: "flex",
                alignItems: "flex-end",
                justifyContent: "flex-end",
                marginTop: "10px",
              }}
            >
              <Button
                flip={true}
                label="Proceed"
                Icon={FiChevronsRight}
                isLoading={isSubmitLoading}
                create={!isEditMode}
                onClick={submitHandler}
                form={HEADER_FORM}
                module={MODULES.Finance}
                authenticated={true}
                editMode={isEditMode}
                disabled={
                  (categoryId === 1 && !bulkGenInvPermission) ||
                  (categoryId === 2 && !bulkGenEvPermission)
                }
              />
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default BulkTaxBreakdown;
